import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import { Chargement, DateHeure, Erreur, formaterDateHeure } from '../../composants/communs.tsx';
import {
  Badge,
  BoutonImprimer,
  DataTable,
  Montant,
  Sheet,
  formaterFcfa,
  exporterTableauPdf,
  type Colonne,
} from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { FormulaireClient } from './FormulaireClient.tsx';
import { Historique } from './Historique.tsx';
import type { ClientEncours, ReponseClients } from './types.ts';

/**
 * Clients et crédit — RG-086 à RG-098.
 *
 * Le plafond de crédit est GLOBAL, toutes boutiques confondues (RG-087). Cet
 * écran n'est donc PAS cloisonné par boutique, et c'est délibéré : afficher un
 * encours partiel donnerait un disponible faux, et un gérant accorderait un
 * crédit que le serveur refuserait ensuite.
 */
export function Clients(): ReactNode {
  const { peut } = useSession();
  const [recherche, setRecherche] = useState('');

  /** `null` : aucun formulaire. `'nouveau'` : création. Sinon : la fiche éditée. */
  const [saisie, setSaisie] = useState<ClientEncours | 'nouveau' | null>(null);
  /** Client dont on consulte l'historique consolidé — RG-197. */
  const [consulte, setConsulte] = useState<ClientEncours | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['clients', recherche],
    queryFn: () =>
      appeler<ReponseClients>(
        '/clients' +
          (recherche.trim() === ''
            ? ''
            : `?q=${encodeURIComponent(recherche.trim())}`),
      ),
  });

  return (
    <>
      <Entete
        titre="Clients et crédit"
        sous="Encours et plafonds, toutes boutiques confondues"
      />

      {consulte !== null && (
        <div style={{ marginBottom: 16 }}>
          <Historique
            clientId={consulte.id}
            nom={`${consulte.nom}${consulte.prenom === null ? '' : ` ${consulte.prenom}`}`}
            onFerme={() => {
              setConsulte(null);
            }}
          />
        </div>
      )}

      {saisie !== null && (
        <div style={{ marginBottom: 16 }}>
          <FormulaireClient
            {...(saisie === 'nouveau' ? {} : { client: saisie })}
            onFerme={() => {
              setSaisie(null);
            }}
          />
        </div>
      )}

      <Sheet>
        {peut('client.creer') && saisie === null && (
          <div style={{ marginBottom: 12 }}>
            <button
              type="button"
              onClick={() => {
                setSaisie('nouveau');
              }}
            >
              Nouveau client
            </button>
          </div>
        )}

        <div className="champ" style={{ marginBottom: 12, maxWidth: 340 }}>
          <label htmlFor="recherche-client">Nom, prénom ou téléphone</label>
          <input
            id="recherche-client"
            value={recherche}
            placeholder="Rechercher…"
            onChange={(evenement) => {
              setRecherche(evenement.target.value);
            }}
          />
        </div>

        <Erreur erreur={error} />
        {isPending ? (
          <Chargement />
        ) : (
          <Liste
            clients={data?.donnees ?? []}
            onModifier={peut('client.modifier') ? setSaisie : null}
            onHistorique={setConsulte}
          />
        )}
      </Sheet>
    </>
  );
}

function Liste({
  clients,
  onModifier,
  onHistorique,
}: {
  clients: ClientEncours[];
  /** `null` quand l'utilisateur n'a pas `client.modifier` : la colonne disparaît. */
  onModifier: ((client: ClientEncours) => void) | null;
  onHistorique: (client: ClientEncours) => void;
}): ReactNode {
  const { ouvrir } = useApercuPdf();
  const colonnes: Colonne<ClientEncours>[] = [
    {
      cle: 'nom',
      entete: 'Client',
      rendu: (client) => `${client.prenom ?? ''} ${client.nom}`.trim(),
      texte: (client) => `${client.prenom ?? ''} ${client.nom}`.trim(),
    },
    {
      cle: 'telephone',
      entete: 'Téléphone',
      rendu: (client) => <span className="amount">{client.telephone ?? '—'}</span>,
      texte: (client) => client.telephone ?? '—',
    },
    {
      cle: 'repere',
      entete: 'Repère',
      // Au Bénin, une adresse formelle est rare : sans le repère, une
      // créance devient irrécouvrable.
      rendu: (client) => client.repere ?? '—',
      texte: (client) => client.repere ?? '—',
    },
    {
      cle: 'plafond',
      entete: 'Plafond',
      numerique: true,
      rendu: (client) =>
        client.plafond_credit === null ? 'Illimité' : <Montant valeur={client.plafond_credit} />,
      texte: (client) =>
        client.plafond_credit === null ? 'Illimité' : formaterFcfa(client.plafond_credit),
    },
    {
      cle: 'encours',
      entete: 'Doit',
      numerique: true,
      rendu: (client) => <Montant valeur={client.encours} />,
      texte: (client) => formaterFcfa(client.encours),
    },
    {
      cle: 'disponible',
      entete: 'Peut prendre',
      numerique: true,
      rendu: (client) =>
        client.plafond_credit === null ? (
          'Illimité'
        ) : (
          <Montant valeur={Math.max(0, client.plafond_credit - client.encours)} />
        ),
      texte: (client) =>
        client.plafond_credit === null
          ? 'Illimité'
          : formaterFcfa(Math.max(0, client.plafond_credit - client.encours)),
    },
    {
      cle: 'dernier',
      entete: 'Dernier règlement',
      rendu: (client) =>
        client.dernier_reglement === null ? (
          '—'
        ) : (
          <DateHeure valeur={client.dernier_reglement} />
        ),
      texte: (client) =>
        client.dernier_reglement === null ? '—' : formaterDateHeure(client.dernier_reglement),
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (client) => <StatutClient client={client} />,
      texte: (client) =>
        !client.actif
          ? 'Inactif'
          : client.echu > 0
            ? 'Échu'
            : client.encours > 0
              ? 'En cours'
              : 'À jour',
    },
    {
      cle: 'actions',
      entete: '',
      rendu: (client: ClientEncours) => (
            <div className="ligne">
              {/*
                L'historique est ouvert a quiconque lit les clients : c'est
                l'ecran qu'on ouvre quand quelqu'un conteste ce qu'il doit, et
                c'est au comptoir que la question se pose.
              */}
              <button
                type="button"
                className="bouton--discret"
                onClick={() => {
                  onHistorique(client);
                }}
              >
                Historique
              </button>
              {/*
                Le masquage est un CONFORT, pas une securite (RG-160) : le
                serveur refuse de toute facon. Cacher un bouton qui menerait a
                un 403 evite simplement une impasse.
              */}
              {onModifier !== null && (
                <button
                  type="button"
                  className="bouton--discret"
                  onClick={() => {
                    onModifier(client);
                  }}
                >
                  Modifier
                </button>
              )}
            </div>
      ),
    },
  ];

  return (
    <>
      {clients.length > 0 && (
        <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
          <BoutonImprimer
            onClick={() => {
              ouvrir(() =>
                exporterTableauPdf({
                  titre: 'Clients et crédit',
                  colonnes,
                  lignes: clients,
                  nomFichier: 'clients',
                }),
              );
            }}
          />
        </div>
      )}
      <DataTable
        lignes={clients}
        cleDe={(client) => client.id}
        vide="Aucun client. Créez-en un, ou laissez les vendeurs le faire depuis l’application."
        colonnes={colonnes}
      />
    </>
  );
}

/**
 * Statut d'un client, par ordre de GRAVITÉ décroissante.
 *
 * Un client désactivé qui doit encore de l'argent doit apparaître comme
 * désactivé — c'est ce qui empêche de lui accorder un nouveau crédit (RG-098)
 * — mais ses créances restent recouvrables. Montrer « échu » en priorité
 * ferait croire qu'on peut encore lui vendre.
 */
function StatutClient({ client }: { client: ClientEncours }): ReactNode {
  const actif = client.actif;

  if (!actif) return <Badge statut="inactif" />;
  if (client.echu > 0) return <Badge statut="echu" />;
  if (client.encours > 0) return <Badge statut="credit" libelle="En cours" />;
  return <Badge statut="a_jour" />;
}
